import { CsvCell } from "ag-grid-community";
import { MetricKey, WindowInfo } from "../api/types";

// ============================================================================
// Legend / methodology — TRUTHFUL to the new reporting engine's code.
//
// Sources (Distributed-Systems/Ronin.ApiService/Endpoints/Reports/Engine):
//   • ReportDefinitions.cs — canonical metric formulas (§ "DECISION-INDEPENDENT metric
//     formulas"), the settled-sale predicate (SettledSaleInWindow), the window/day-boundary
//     (decision 4=C), the approved/refunded/fake-money tender predicates, and decisions 1/2/3.
//   • OrderGrainEngine.cs — the inline metric sums and the per-order real-money Net cap
//     (EventSummaryAsync + RealMoneyNetByEventAsync).
// These strings paraphrase that code for a non-engineer; they do NOT invent idealized formulas.
// ============================================================================

export interface MetricDefinition {
  label: string;
  formula: string; // concise, matches the engine's actual computation
  explanation: string; // plain-English for an ops user
}

// Legacy-token caveat (contract §1): legacy token spend carried NO transaction row (it was
// money-less), so the Token tender columns read ~$0 on migrated/historical events and only
// populate for go-forward wallet-linked token spends. Migration-completeness gap, not a report
// bug — the column is shown, not suppressed. Surfaced as a grid footnote + in the CSV legend.
export const TOKEN_TENDER_CAVEAT =
  "Token columns read ~$0 on events migrated from the legacy system: legacy token spend carried " +
  "no transaction row. This is a data-migration gap, not a report bug; go-forward wallet-linked " +
  "token spends do populate here.";

// Untendered legend (RP-2155/2159). The new Gross is SUPPOSED to exceed the old report's
// "Total Gross Sales", because the old figure was defined as the sum of its tender columns and so
// silently dropped every settled sale no approved tender covered. Without this on screen the new
// column reads as a defect, which is what bounced the ticket. Shown on the Financial group-by page
// (gated on TenderUntenderedAmount being advertised) and prepended to its CSV.
export const UNTENDERED_LEGEND =
  "Untendered is the sales portion no approved tender covered (migrated no-tender and " +
  "under-tendered orders). Tender columns + Untendered = Gross Sales — this is why the old " +
  "report's Total Gross Sales, which summed only the tender columns, reads lower.";

// Same fact for a section that lists tender classes ONLY (Vertical Sales' Payment Breakdown, page
// and PDF). Untendered is deliberately not a payment row there — it is not a tender class — so the
// section's total sits under a Gross card it no longer equals whenever Untendered != 0.
export const UNTENDERED_TENDER_ONLY_NOTE =
  "Tender classes only: this excludes Untendered (the sales portion no approved tender covered), " +
  "so the total is Gross Sales minus Untendered and the percentages are shares of tender sales, " +
  "not of Gross.";

type TenderMetricKey = Extract<MetricKey, `Tender${string}`>;

// One amount+count definition pair per tender class, mirroring the old reports' buckets.
// The loop below emits Amount+Count for all seven classes — exactly the TenderMetricKey set.
function tenderDefinitions(): Record<TenderMetricKey, MetricDefinition> {
  const classes: { key: string; name: string; countName?: string; what: string; note?: string }[] = [
    { key: "TenderCash", name: "Cash", what: "raw cash tenders" },
    { key: "TenderCredit", name: "Credit", what: "card/credit tenders (present + not-present)" },
    { key: "TenderRfid", name: "RFID", what: "RFID band taps (unless funded by a token/promo wallet)" },
    { key: "TenderCashBalance", name: "Cash Balance", what: "pre-loaded real-money balance spend" },
    { key: "TenderPromoBalance", name: "Promo Balance", what: "promo/comp balance spend (not real money)" },
    { key: "TenderToken", name: "Token", countName: "Tokens", what: "token-wallet spend (not real money)", note: ` ${TOKEN_TENDER_CAVEAT}` },
    { key: "TenderOther", name: "Other", what: "defunct/unknown tenders (Tappit, QR Code)" },
  ];
  const defs: Partial<Record<TenderMetricKey, MetricDefinition>> = {};
  for (const c of classes) {
    defs[`${c.key}Amount` as TenderMetricKey] = {
      label: `Gross ${c.name} Sales`,
      formula: `Σ approved transaction amounts classed as ${c.name}, capped per order at Subtotal + Tax`,
      explanation:
        `The sales portion tendered via ${c.what}. Tips and the service fee are NEVER part of a ` +
        `gross sales column — each order's tendered money is capped at its Subtotal + Tax (like the ` +
        `legacy gross bucket), so the tender columns sum to Gross Sales. Tips/fee stay in their own ` +
        `columns.${c.note ?? ""}`,
    };
    defs[`${c.key}Count` as TenderMetricKey] = {
      label: `# of Transactions - ${c.countName ?? c.name}`,
      formula: `count of approved transactions classed as ${c.name}`,
      explanation: `Number of approved transactions via ${c.what}. Tender counts sum to Transactions.${c.note ?? ""}`,
    };
  }
  return defs as Record<TenderMetricKey, MetricDefinition>;
}

export const METRIC_DEFINITIONS: Record<MetricKey, MetricDefinition> = {
  // Order grain — ReportDefinitions §formulas; OrderGrainEngine sums (Subtotal+Tax), Tax, Tip, DigitalSurcharge.
  Gross: {
    label: "Gross Sales",
    formula: "Σ (order subtotal + order tax)",
    explanation:
      "Money taken for product: the items' subtotal plus tax. Tips and the service fee are NOT included. " +
      "A refunded sale stays in Gross (the sale still happened) and is instead netted out of Net Sales.",
  },
  // Net — decision 1=B + the real-money cap. OrderGrainEngine.RealMoneyNetByEventAsync:
  //   productNet = Σ(LineSubtotal − LineDiscount) − OrderDiscount − pre-tax refund portion;
  //   Net(order) = min(productNet, realMoney); realMoney = Σ approved − Σ approved fake-money tenders.
  NetSales: {
    label: "Net Sales",
    formula:
      "per order: min( Σ(line subtotal − line discount) − order discounts − pre-tax refund, real money collected ); summed over orders",
    explanation:
      "Real-money product revenue. For each order, start from item subtotal, subtract item discounts, " +
      "whole-order discounts, and the pre-tax portion of any refund — then CAP the result at the real money " +
      "actually collected on that order. Orders paid only with tokens or promo balance contribute $0; " +
      "partly-real orders keep only the real-money share. Tax, tips, and service fee are not part of Net.",
  },
  Tax: {
    label: "Tax",
    formula: "Σ order tax",
    explanation: "Total tax charged across the included orders.",
  },
  Tip: {
    label: "Tips",
    formula: "Σ order tip",
    explanation: "Total tips. Reported on their own — never part of Gross or Net Sales.",
  },
  Surcharge: {
    label: "Service Fee",
    formula: "Σ order digital surcharge",
    explanation:
      "The digital/service fee. Reported on its own — never part of Gross or Net Sales.",
  },
  // Discount — decision 2=B: line discounts + whole-order discounts.
  Discount: {
    label: "Discounts",
    formula: "Σ item line discounts + Σ whole-order discounts",
    explanation:
      "All discounts: per-item line discounts plus whole-order promos/comps (which earlier reports didn't capture).",
  },
  // Refunds — decision 3=B: Σ refunded-transaction amount (gross returned).
  Refunds: {
    label: "Refunds",
    formula: "Σ refunded transaction amounts",
    explanation:
      "Gross amount returned to customers (refunded transactions). Shown on its own line; the product portion " +
      "of the refund is netted out of Net Sales.",
  },
  OrderCount: {
    label: "Orders",
    formula: "count of distinct included orders",
    explanation: "Number of settled product-sale orders in the window (see inclusion rules).",
  },
  // TransactionCount — ReportDefinitions.ApprovedTransaction(): Status == Approved, on included orders.
  TransactionCount: {
    label: "Transactions",
    formula: "count of approved transactions on included orders",
    explanation: "Number of approved payment transactions on the included orders.",
  },

  // Item grain — ReportDefinitions §formulas (Units, ItemRevenue, ItemTax, ItemDiscount).
  Units: {
    label: "Units Sold",
    formula: "Σ order-item quantity",
    explanation: "Total quantity of items sold.",
  },
  ItemRevenue: {
    label: "Item Revenue",
    formula: "Σ order-item line subtotal",
    explanation: "Item revenue before tax and before discounts.",
  },
  ItemTax: {
    label: "Item Tax",
    formula: "Σ order-item line tax",
    explanation: "Tax attributed to the line items.",
  },
  ItemDiscount: {
    label: "Item Discount",
    formula: "Σ order-item line discount",
    explanation: "Item-level discounts only (whole-order discounts are not included in this figure).",
  },
  ItemGross: {
    label: "Gross",
    formula: "Item Revenue + Item Tax",
    explanation: "Per-line gross for product mix: item revenue plus item tax.",
  },

  // Transaction grain — Σ approved transaction amount (money COLLECTED, deliberately uncapped).
  TransactionAmount: {
    label: "Transaction Amount",
    formula: "Σ approved transaction amounts (money as collected)",
    explanation:
      "Total money collected by approved payment transactions — INCLUDES tips and the service fee, " +
      "because that is what was actually charged. A payment figure, not a sales figure: it does not " +
      "tie to Gross Sales (for a fully-paid scope it is ≈ Gross + Tips + Service Fee).",
  },

  // Per-tender breakdown (RP-2153…59) — ReportDefinitions.ClassifyTender / TenderMetricMap.
  // Amounts are CAPPED per order at Subtotal + Tax (ReportDefinitions.CapTenderAmounts): tips and
  // the service fee are never in a gross sales column, matching the legacy proc's capped
  // gross_*_sales buckets — so the tender columns sum to Gross. Counts partition Transactions.
  // Classification is funding-source aware: token/promo WALLET spends class as Tokens/Promo Balance
  // even when the sale transaction says RFID; an RFID tap funded by cash balance stays RFID. The
  // legacy split_payment flag is deliberately not used.
  ...tenderDefinitions(),

  // ---- Financial decompositions (RP-2174 Ticketing Financial) — ADDITIVE, reconcile to the canonical
  // metrics above; they never redefine Gross/Net/Discount/Refunds/Tip. ----
  DiscountExPromo: {
    label: "Net Deduction - Discounts",
    formula: "Discounts − Promo Codes (item line discounts + non-code whole-order discounts)",
    explanation:
      "The discounts that are NOT a customer promo code: per-item line discounts plus admin/whole-order " +
      "discounts with no redemption code. Together with Promo Codes this sums to the total Discounts.",
  },
  PromoDiscount: {
    label: "Net Deduction - Promo Codes",
    formula: "Σ whole-order discounts whose discount carries a redemption Code",
    explanation:
      "The share of discounts redeemed via a customer promo code (the discount record has a Code). " +
      "Discounts + Promo Codes = the total Discounts figure.",
  },
  RefundsPreTax: {
    label: "Net Deduction - Refunds",
    formula: "per order: gross refund × (Subtotal / GrandTotal), clamped to the product value",
    explanation:
      "The pre-tax PRODUCT portion of refunds — the exact amount netted out of Net Sales. Derived by " +
      "pro-rating the gross refund by the order's product fraction (transactions store no separate refunded " +
      "tax). Always between 0 and the gross Total Refunds Value.",
  },
  MerchantProcessed: {
    label: "Total Merchant Processed",
    formula:
      "Σ approved (Credit+RFID) transaction amounts − Σ refunded (Credit+RFID) transaction amounts",
    explanation:
      "The ACTUAL money the processor settled on card/RFID: the real amounts charged less the real amounts " +
      "refunded on those tenders. A payment figure (uncapped — it can exceed the capped card sales columns); " +
      "tender-less orders contribute $0. May be negative in a refund-heavy (net-reversal) period.",
  },
  TenderUntenderedAmount: {
    label: "Untendered",
    formula: "Gross − Σ (capped tender amounts), per order",
    explanation:
      "The sales portion (Subtotal + Tax) that no approved tender covered — settled sales kept in Gross with " +
      "no or partial tender (e.g. the migration no-tender gap). Surfaced so the tender columns reconcile: the " +
      "tender sales plus Untendered equal Gross.",
  },
  NetPromoBalanceSales: {
    label: "Net Deduction - Promo Balance Sales",
    formula: "Σ promo-balance-funded sales removed from Net Sales",
    explanation:
      "The promo/comp-balance share of sales that Net Sales excludes (promo balance is not real money). " +
      "The legacy Vendor Sales report showed it as an explicit net deduction; here Net Sales already has " +
      "it removed by the real-money cap, so this column is the amount that cap took out, shown for parity.",
  },
  TenderCreditTip: tenderTipDef("Credit", "card/credit tenders"),
  TenderCashTip: tenderTipDef("Cash", "raw cash tenders"),
  TenderCashBalanceTip: tenderTipDef("Cash Balance", "pre-loaded real-money balance spend"),
  TenderRfidTip: tenderTipDef("RFID", "RFID band taps"),
  TenderPromoBalanceTip: tenderTipDef("Promo Balance", "promo/comp balance spend"),
  TenderTokenTip: tenderTipDef("Tokens", "token-wallet spend"),
  TenderOtherTip: tenderTipDef("Other", "defunct/unknown tenders"),
};

// One per-tender TIP definition. Tips are the order's total tip attributed across tender classes by the
// tip+fee "excess over the sales cap" the tender waterfall relocates onto the card/digital legs; Σ == Tips.
function tenderTipDef(name: string, what: string): MetricDefinition {
  return {
    label: `Tips - ${name}`,
    formula: `order tip allocated to ${name} by each tender's excess over its capped sales portion`,
    explanation:
      `The share of tips attributed to ${what}. Tips are split across tenders so the per-tender tips sum ` +
      `exactly to the total Tips; the card/RFID legs feed Merchant Processed.`,
  };
}

export interface InclusionRule {
  title: string;
  body: string;
}

// Report-wide rules — truthful to ReportDefinitions.SettledSaleInWindow, the wallet-load denylist,
// decision 4=C (5 AM business day), the refund stance, the real-money cap, and ApprovedTransaction().
export const INCLUSION_RULES: InclusionRule[] = [
  {
    title: "Which sales count",
    body:
      "An order is included when it is SETTLED (status Completed or Refunded), is a PRODUCT SALE (wallet " +
      "loads — cash/promo/token loads and wristband transfers/combines — are excluded), and was PAID within " +
      "the window (it has a payment timestamp). Voided and Cancelled orders are excluded.",
  },
  {
    title: "Business day (window boundary)",
    body:
      "When the report is scoped to a single event or location, days run 5:00 AM → 5:00 AM in that location's " +
      "local time (daylight-saving aware): a sale before 5 AM counts on the previous business day. When no single " +
      "timezone applies, the interim boundary is the UTC calendar day. The exact boundary used is shown at the top " +
      "of each report.",
  },
  {
    title: "Refunds",
    body:
      "Refunded sales STAY in Gross (the sale happened); the pre-tax product portion of the refund is subtracted " +
      "from Net Sales. A refund can only reduce Net, never increase it.",
  },
  {
    title: "Real money only (tokens & promo balance)",
    body:
      "Net Sales counts real money only. Tenders funded by promo balance or tokens are treated as 'fake money' and " +
      "excluded; each order's Net is capped at the real money (cash, card, cash-balance) actually collected. Such " +
      "orders still appear in Gross.",
  },
  {
    title: "Approved transactions",
    body: "A transaction counts when its status is Approved. (Refunded transactions feed the Refunds line.)",
  },
];

const cell = (value: string): CsvCell => ({ data: { value } });

// Builds the "how these are calculated" block prepended to every CSV export, as CsvCell rows
// (ag-grid CSV-quotes each cell, so commas in the prose stay in one column).
export function buildMethodologyCsvRows(
  metrics: MetricKey[],
  window?: WindowInfo
): CsvCell[][] {
  const rows: CsvCell[][] = [];
  rows.push([cell("HOW THESE NUMBERS ARE CALCULATED — new reporting engine")]);
  if (window) {
    rows.push([cell(`Window: ${window.from} to ${window.to}`)]);
    rows.push([cell(`Day boundary: ${window.dayBoundary}`)]);
  }
  rows.push([cell("")]);

  rows.push([cell("METRICS")]);
  for (const m of metrics) {
    const d = METRIC_DEFINITIONS[m];
    if (!d) continue;
    rows.push([cell(`${d.label} = ${d.formula}`)]);
    rows.push([cell(`    ${d.explanation}`)]);
  }
  rows.push([cell("")]);

  rows.push([cell("INCLUSION RULES")]);
  for (const r of INCLUSION_RULES) {
    rows.push([cell(`${r.title}: ${r.body}`)]);
  }
  rows.push([cell("")]);
  return rows;
}
